import React from 'react';
import { cn } from '@core/utils/cn';
import LanguageSelect from '../language-select';
import { useLocalizedField } from '@components/hooks/use-localised-field';
import { I18NText } from '@api/ChallengeClient';
import { I18n } from '@core/i18n';

type Variant = 'body1' | 'body2' | 'caption' | 'h6' | 'subtitle1' | 'subtitle2';

interface LocalizedTextDisplayProps {
    value?: I18NText;
    label?: string;
    variant?: Variant;
    color?: string;
    emptyText?: string;
}

const variantClasses: Record<Variant, string> = {
    body1: 'text-base',
    body2: 'text-sm',
    caption: 'text-xs',
    h6: 'text-lg font-semibold',
    subtitle1: 'text-base font-medium',
    subtitle2: 'text-sm font-medium'
};

const colorClasses: Record<string, string> = {
    'text.secondary': 'text-muted-foreground',
    error: 'text-destructive',
    warning: 'text-warning',
    success: 'text-success',
    info: 'text-info',
    primary: 'text-primary'
};

export const LocalizedTextDisplay = ({
    value,
    label,
    variant = 'body2',
    color = 'text.primary',
    emptyText = 'No content available'
}: LocalizedTextDisplayProps) => {
    const { options, language, setLanguage } = useLocalizedField(
        value || {},
        I18n.allLanguageOptions,
        I18n.mainLanguageKey
    );

    const hasContent =
        value &&
        Object.values(value).some((text) => text && text.trim().length > 0);

    if (!hasContent) {
        return (
            <div>
                {label && (
                    <span className="mb-1 block text-xs text-muted-foreground">
                        {label}
                    </span>
                )}
                <p
                    className={cn(
                        variantClasses[variant],
                        'text-muted-foreground italic'
                    )}
                >
                    {emptyText}
                </p>
            </div>
        );
    }

    return (
        <div>
            {label && (
                <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                        {label}
                    </span>
                    <LanguageSelect
                        options={options}
                        value={language}
                        onChange={setLanguage}
                    />
                </div>
            )}

            {value &&
                Object.entries(value).map(([key, text]) => (
                    <div key={key} className={cn(language !== key && 'hidden')}>
                        <p
                            className={cn(
                                'whitespace-pre-wrap',
                                variantClasses[variant],
                                colorClasses[color] ?? 'text-foreground'
                            )}
                        >
                            {text || (
                                <span className="text-muted-foreground italic">
                                    No {key.toUpperCase()} translation
                                </span>
                            )}
                        </p>
                    </div>
                ))}
        </div>
    );
};

export default LocalizedTextDisplay;
